import { useCallback, useMemo } from "react";
import { useQueryClient } from "react-query";
import {
  ReleaseNotesItem,
  useLegolandApiClient,
  useReleaseNotesProductId,
  useReleaseNotesQuery,
} from "../legolandApi";

export interface ReleaseNotesStatus {
  isDismissed: boolean | undefined;
  isRead: boolean | undefined;
}

const defaultReleaseNotesStatus: ReleaseNotesStatus = {
  isDismissed: undefined,
  isRead: undefined,
};

export function useReleaseNotesStatus({
  productId,
}: { productId?: string } = {}): ReleaseNotesStatus {
  productId = useReleaseNotesProductId(productId);

  const rnQuery = useReleaseNotesQuery(
    { productId },
    { refetchInterval: 10 * 60 * 1000 }
  );

  const rnIds = useMemo(
    () => rnQuery.data?.map((rn) => rn.id),
    [rnQuery.data, rnQuery.isStale]
  );

  const seenIds = useMemo(
    () =>
      productId && rnIds && getReleaseNotesStatusIds(productId, "seen", rnIds),
    [productId, rnIds]
  );

  const dismissedIds = useMemo(
    () =>
      productId &&
      rnIds &&
      getReleaseNotesStatusIds(productId, "dismissed", rnIds),
    [productId, rnIds]
  );

  if (!rnIds || !seenIds || !dismissedIds) {
    return defaultReleaseNotesStatus;
  }

  if (rnQuery.error) {
    console.error(rnQuery.error);
    return defaultReleaseNotesStatus;
  }

  if (!rnQuery.data?.length) {
    return { isDismissed: true, isRead: true };
  }

  const isRead = rnIds.find((id) => !seenIds.includes(id)) === undefined;

  const isDismissed =
    isRead || rnIds.find((id) => !dismissedIds.includes(id)) === undefined;

  return { isDismissed, isRead };
}

export function useReleaseNotesMarkRead({
  productId,
}: { productId?: string } = {}) {
  const apiClient = useLegolandApiClient();
  const queryClient = useQueryClient();

  productId = useReleaseNotesProductId(productId);

  const markRead = useCallback(
    (releaseNotes: ReleaseNotesItem[]) => {
      if (!productId) throw new Error("No product id provided.");
      setReleaseNotesStatus(productId, "seen", releaseNotes);
      queryClient.invalidateQueries(["legoland", "release-notes", productId]);
    },
    [apiClient, productId]
  );

  return markRead;
}

export function useReleaseNotesMarkDismissed({
  productId,
}: { productId?: string } = {}) {
  const apiClient = useLegolandApiClient();
  const queryClient = useQueryClient();

  productId = useReleaseNotesProductId(productId);

  const markDismissed = useCallback(
    (releaseNotes: ReleaseNotesItem[]) => {
      if (!productId) throw new Error("No product id provided.");
      setReleaseNotesStatus(productId, "dismissed", releaseNotes);
      queryClient.invalidateQueries(["legoland", "release-notes", productId]);
    },
    [apiClient, productId]
  );

  return markDismissed;
}

const rootRnStorageKey = "legolandSdk.releaseNotes";

function getReleaseNotesStatusIds(
  productId: string,
  type: "seen" | "dismissed",
  defaultIds: number[]
) {
  const storageKey = `${rootRnStorageKey}.${productId}.${type}Ids`;
  const idsJson = localStorage.getItem(storageKey);
  let ids: number[];

  if (idsJson === null) {
    ids = defaultIds;
    // Do not save the state when there are no release notes. This is to prevent
    // notifying about new release notes when we first migrate to the new system.
    // The condition can be removed after the migration is complete.
    if (defaultIds.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(ids));
    }
  } else {
    ids = JSON.parse(idsJson);
  }

  return ids;
}

function setReleaseNotesStatus(
  productId: string,
  type: "seen" | "dismissed",
  releaseNotes: ReleaseNotesItem[]
) {
  if (!releaseNotes.length) return; // The migration to the new system did not happen yet. Remove after migration is complete, so that new products receive proper notifications.

  const rnIds = releaseNotes.map((rn) => rn.id);
  const ids = getReleaseNotesStatusIds(productId, type, rnIds);

  for (const id of rnIds) {
    if (!ids.includes(id)) {
      ids.push(id);
    }
  }

  localStorage.setItem(
    `${rootRnStorageKey}.${productId}.${type}Ids`,
    JSON.stringify(ids)
  );
}
